import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mic, X, Pencil } from "lucide-react";
import { API, useAuth } from "../App";
import { useRadio } from "./RadioContext";
import { HearEpisodeButton } from "./HearIt";
import { MomentButton } from "./MomentLink";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }, withCredentials: true });

// Title + "what you'll hear" note under a clip pill; admin/magician click the pencil to edit in place.
const ClipNote = ({ ep, isAdmin, dark, onSaved }) => {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ title: ep.title, description: ep.description || "" });
  const [busy, setBusy] = useState(false);
  const sub = dark ? "text-white/70" : "text-[#5C5C5C]";
  const save = async () => {
    if (form.title.trim().length < 2) return toast.error("Give the clip a title");
    setBusy(true);
    try { await axios.put(`${API}/admin/radio/episodes/${ep.episode_id}`, { title: form.title.trim(), description: form.description.trim(), episode_number: ep.episode_number }, auth()); setEdit(false); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); } finally { setBusy(false); }
  };
  if (edit) {
    return (
      <div className="mt-1.5 space-y-1" data-testid={`clip-note-editor-${ep.slug}`}>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Clip title" className="w-full rounded-md border border-[#E2E0DC] px-2 py-1 text-xs text-[#1A1A1A]" data-testid={`clip-note-title-${ep.slug}`} />
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="What they'll hear — a sentence or two shown under the button" className="w-full rounded-md border border-[#E2E0DC] px-2 py-1 text-xs text-[#1A1A1A]" data-testid={`clip-note-text-${ep.slug}`} />
        <div className="flex gap-2">
          <button type="button" onClick={save} disabled={busy} className="rounded-full bg-[#2E5C55] text-white px-3 py-1 text-[11px] font-bold" data-testid={`clip-note-save-${ep.slug}`}>{busy ? "Saving…" : "Save"}</button>
          <button type="button" onClick={() => { setEdit(false); setForm({ title: ep.title, description: ep.description || "" }); }} className={`text-[11px] underline ${sub}`}>Cancel</button>
        </div>
      </div>
    );
  }
  return (
    <div className="mt-1 group" data-testid={`clip-note-${ep.slug}`}>
      <p className={`m-0 text-sm sm:text-base font-black leading-snug ${dark ? "text-white" : "text-[#1A1A1A]"}`} data-testid={`clip-title-${ep.slug}`}>
        {ep.title}
        {isAdmin && <button type="button" onClick={() => setEdit(true)} className={`ml-1.5 inline-flex align-middle opacity-60 hover:opacity-100 ${sub}`} aria-label="Edit title and note" title="Edit the title and the note under the button" data-testid={`clip-note-edit-${ep.slug}`}><Pencil className="h-3 w-3" /></button>}
      </p>
      {ep.description ? <p className={`m-0 mt-1 text-sm font-bold leading-snug ${dark ? "text-white/85" : "text-[#2B2B2B]"}`} data-testid={`clip-desc-${ep.slug}`}>{ep.description}</p>
        : isAdmin && <button type="button" onClick={() => setEdit(true)} className={`m-0 mt-0.5 text-[11px] italic underline ${sub}`} data-testid={`clip-note-add-${ep.slug}`}>+ add a note on what they'll hear</button>}
    </div>
  );
};

// Voiced summaries for one section of a page (e.g. a year in Our Origins). Each clip is a short
// Range Free Radio episode attached to `attach` ({kind, slug, name}); `moments` adds Hear-it buttons
// that point at stable moment keys inside bigger episodes. Admin/magician get "Record a clip" + remove.
export const ChapterVoice = ({ attach, moments = [], back, heading, dark = false, className = "", onClips }) => {
  const { user } = useAuth();
  const r = useRadio();
  const [eps, setEps] = useState([]);
  const isAdmin = user?.role === "admin" || user?.role === "magician";
  const key = `${attach.kind}:${attach.slug}`;

  const load = useCallback(() => {
    const req = isAdmin ? axios.get(`${API}/radio/episodes`, { params: { attached: key, all: 1 }, ...auth() }) : axios.get(`${API}/radio/episodes`, { params: { attached: key } });
    req.then((res) => { const list = (res.data || []).slice().reverse(); setEps(list); onClips?.(list); }).catch(() => { setEps([]); onClips?.([]); });
  }, [key, isAdmin]);
  useEffect(load, [load]);

  const detach = async (ep) => {
    if (!window.confirm(`Take "${ep.title}" off this section? (The recording stays in the Radio panel.)`)) return;
    try { await axios.put(`${API}/admin/radio/episodes/${ep.episode_id}`, { title: ep.title, description: ep.description || "", episode_number: ep.episode_number, attached_to: {} }, auth()); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't remove"); }
  };

  const keys = (moments || []).filter((m) => m?.key);
  if (!isAdmin && eps.length === 0 && keys.length === 0) return null;
  const studio = `/admin/studio?attach=${encodeURIComponent(key)}&name=${encodeURIComponent(attach.name || attach.slug)}${back ? `&back=${encodeURIComponent(back)}` : ""}`;

  return (
    <div className={`mt-4 ${className}`} data-testid={`chapter-voice-${attach.slug}`}>
      <div className={`text-[11px] font-bold uppercase tracking-[0.25em] ${dark ? "text-white/60" : "text-[#5C5C5C]"}`}>{heading || r.labels.chapter_heading}</div>
      <div className="mt-2 flex flex-col items-start gap-2">
        {eps.map((ep) => (
          <div key={ep.episode_id} className="max-w-full">
            <div className="flex items-center gap-1">
              <HearEpisodeButton episode={ep} showTitle={false} className="whitespace-nowrap" testId={`chapter-voice-clip-${ep.slug}`} />
              {isAdmin && !ep.published && <span className="text-[10px] font-black text-amber-600" title="Draft — publish it in Admin → Radio">DRAFT</span>}
              {isAdmin && <button type="button" onClick={() => detach(ep)} className="text-red-500/80 hover:text-red-500 p-1" aria-label="Remove clip from this section" data-testid={`chapter-voice-remove-${ep.slug}`}><X className="h-3.5 w-3.5" /></button>}
            </div>
            <ClipNote ep={ep} isAdmin={isAdmin} dark={dark} onSaved={load} />
          </div>
        ))}
        {keys.map((m) => (
          <div key={m.key} className="max-w-full">
            <MomentButton momentKey={m.key} label={m.label || undefined} showTitle={!m.label} className="whitespace-nowrap" testId={`chapter-voice-moment-${m.key}`} />
            {m.note && <p className={`m-0 mt-1 text-sm font-bold leading-snug ${dark ? "text-white/85" : "text-[#2B2B2B]"}`}>{m.note}</p>}
          </div>
        ))}
        {isAdmin && (
          <Link to={studio} className={`inline-flex items-center gap-1.5 rounded-full border border-dashed px-3 py-1.5 text-[11px] font-bold no-underline ${dark ? "border-white/40 text-white/80 hover:bg-white/10" : "border-[#2E5C55]/50 text-[#2E5C55] hover:bg-[#2E5C55]/10"}`} data-testid={`chapter-voice-record-${attach.slug}`}>
            <Mic className="h-3 w-3" /> {eps.length ? "Record another clip" : "Record a clip"}
          </Link>
        )}
      </div>
    </div>
  );
};
